import * as React from 'react';
import axiosInstance from 'utils/axiosInterceptors';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { Form, Col, Row, Stack, ButtonGroup } from 'react-bootstrap';
import { ColumnDef } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TLocations } from 'utils/types';
import {
  faEdit,
  faList,
  faMap,
  faMapLocation
} from '@fortawesome/free-solid-svg-icons';
import { useBranchContext } from 'providers/BranchProvider';
import CustomModal from 'components/modals/CustomModal';
import DataTable from 'pages/apps/crm/BranchProductInventory/DataTable';
import Button from 'components/base/Button';
import classNames from 'classnames';
import { useAuth } from 'hooks/useAuth';
import { FeatureRoles, hasAnyRole } from 'utils/roleAccess';
import { useQueryClient } from '@tanstack/react-query';
import { toastConfig } from 'utils/toastConfig';
import useGetLocations from 'pages/apps/hooks/useGetLocations';
import LocationMapView from './Locations/LocationMapView';

type LocationDetailsFormProps = {
  locationDetails: TLocations;
  onChange: <K extends keyof TLocations>(
    field: K,
    value: TLocations[K]
  ) => void;
};

const LocationDetailsForm = React.memo(
  ({ locationDetails, onChange }: LocationDetailsFormProps) => {
    return (
      <Form>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="locationName">
            <Form.Label>Location Name</Form.Label>
            <Form.Control
              type="text"
              value={locationDetails.locationName || ''}
              onChange={e => onChange('locationName', e.target.value)}
            />
          </Form.Group>
        </Row>
      </Form>
    );
  }
);

const LocationList = () => {
  const addModalRef = React.useRef<{ show: () => void; hide: () => void }>({
    show: () => {},
    hide: () => {}
  });
  const editModalRef = React.useRef<{ show: () => void; hide: () => void }>({
    show: () => {},
    hide: () => {}
  });
  const { selectedBranch } = useBranchContext();
  const { token } = useAuth();
  const isAdmin = hasAnyRole(token?.roles, FeatureRoles.adminOnly);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'list' ? 'list' : 'map';
  const selectedLocationId = Number(searchParams.get('locationId')) || null;
  const [locationDetails, setLocationDetails] = React.useState({
    locationId: 0,
    locationName: ''
  });

  const {
    locations,
    loading: locationsLoading,
    refetch: refetchLocations
  } = useGetLocations({
    branchDomain: selectedBranch?.branchDomain as string,
    currentPage: 1,
    pageSize: 99
  });

  const setView = (nextView: 'map' | 'list') => {
    const next = new URLSearchParams(searchParams);
    next.set('view', nextView);
    if (nextView === 'list') next.delete('locationId');
    setSearchParams(next);
  };

  const setSelectedLocationId = (locationId: number | null) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'map');
    if (locationId) next.set('locationId', String(locationId));
    else next.delete('locationId');
    setSearchParams(next);
  };

  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    if (selectedBranch?.branchDomain) {
      const { data } = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain as string}/locations`,
        {
          params: {
            page,
            pageSize,
            search,
            orderBy,
            orderByDesc
          }
        }
      );

      return data;
    }
    return null;
  };

  const refreshLocations = () => {
    void queryClient.invalidateQueries({
      queryKey: ['location', selectedBranch?.branchDomain]
    });
    void refetchLocations();
  };

  const handleSubmit = async () => {
    try {
      await axiosInstance.post(
        `/api/v1/${selectedBranch?.branchDomain}/locations`,
        {
          locationName: locationDetails.locationName
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      setLocationDetails({ locationId: 0, locationName: '' });
      addModalRef.current.hide();
      refreshLocations();
      toast.success('Location Successfully Added', toastConfig);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.message, { ...toastConfig, autoClose: 3000 });
      }
    }
  };

  const handleEditLocation = async () => {
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/locations/${locationDetails.locationId}`,
        {
          locationName: locationDetails.locationName
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      editModalRef.current.hide();
      refreshLocations();
      toast.success('Location Edited', toastConfig);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.message, { ...toastConfig, autoClose: 3000 });
      }
    }
  };

  const columns: ColumnDef<TLocations>[] = [
    {
      id: 'locationName',
      accessorKey: 'locationName',
      enableSorting: true,
      header: 'Location Name',
      cell: ({ row: { original } }) => {
        const { locationName, locationId } = original;
        return (
          <button
            type="button"
            className="btn btn-link text-decoration-none fw-bold fs-8 p-0"
            onClick={() => setSelectedLocationId(locationId)}
          >
            {locationName}
          </button>
        );
      }
    },
    ...(isAdmin
      ? [
          {
            header: 'Edit',
            accessorKey: 'editProduct',
            enableSorting: false,
            cell: ({ row: { original } }: { row: { original: TLocations } }) => {
              return (
                <FontAwesomeIcon
                  icon={faEdit}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    handleEdit(original);
                  }}
                />
              );
            }
          } as ColumnDef<TLocations>
        ]
      : [])
  ];

  const handleEdit = (location: TLocations) => {
    setLocationDetails(location);
    editModalRef.current.show();
  };
  const handleAddLocation = () => {
    setLocationDetails({
      locationId: 0,
      locationName: ''
    });
    addModalRef.current.show();
  };

  const handleFormChange = <K extends keyof TLocations>(
    field: K,
    value: TLocations[K]
  ) => {
    setLocationDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <>
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-center mb-3 flex-wrap gap-2"
      >
        <h4 className="mb-0">Locations</h4>
        <div className="d-flex align-items-center gap-2">
          <ButtonGroup size="sm">
            <Button
              variant="phoenix-secondary"
              className={classNames({ active: view === 'map' })}
              startIcon={<FontAwesomeIcon icon={faMap} />}
              onClick={() => setView('map')}
            >
              Map
            </Button>
            <Button
              variant="phoenix-secondary"
              className={classNames({ active: view === 'list' })}
              startIcon={<FontAwesomeIcon icon={faList} />}
              onClick={() => setView('list')}
            >
              List
            </Button>
          </ButtonGroup>
          {isAdmin && (
            <Button
              variant="primary"
              onClick={handleAddLocation}
              startIcon={
                <FontAwesomeIcon
                  icon={faMapLocation}
                  style={{ cursor: 'pointer' }}
                />
              }
            >
              Add Location
            </Button>
          )}
        </div>
      </Stack>

      {view === 'map' ? (
        <LocationMapView
          branchDomain={selectedBranch?.branchDomain}
          locations={locations}
          locationsLoading={locationsLoading}
          selectedLocationId={selectedLocationId}
          onSelectLocation={setSelectedLocationId}
          isAdmin={isAdmin}
        />
      ) : (
        <div className="mb-6">
          <DataTable
            queryKey={['location', selectedBranch?.branchDomain]}
            queryFn={fetchData}
            columns={columns}
          />
        </div>
      )}
      {isAdmin && (
        <>
          <CustomModal
            ref={addModalRef}
            title="Add Location"
            content={
              <LocationDetailsForm
                locationDetails={locationDetails}
                onChange={handleFormChange}
              />
            }
            onCancel={() => addModalRef.current.hide()}
            onSubmit={() => handleSubmit()}
          />
          <CustomModal
            ref={editModalRef}
            title="Edit Location"
            content={
              <LocationDetailsForm
                locationDetails={locationDetails}
                onChange={handleFormChange}
              />
            }
            onCancel={() => editModalRef.current.hide()}
            onSubmit={() => handleEditLocation()}
          />
        </>
      )}
    </>
  );
};

export default LocationList;
